'use client';
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import TTOSHeader from './TTOSHeader';
import TTOSFooter from './TTOSFooter';

// Variant 1 Components (Screenshot 1)
import TTOSHeroV1 from './TTOSHeroV1';
import TTOSIndustrySelector from './TTOSIndustrySelector';
import TTOSWayProcess from './TTOSWayProcess';
import TTOSEverythingGrid from './TTOSEverythingGrid';
import TTOSStatsRibbon from './TTOSStatsRibbon';
import TTOSTestimonials from './TTOSTestimonials';
import TTOSCTABanner from './TTOSCTABanner';

// Variant 2 Components (Screenshot 2 & 3)
import TTOSHeroV2 from './TTOSHeroV2';
import TTOSFeaturesV2 from './TTOSFeaturesV2';
import TTOSIndustriesV2 from './TTOSIndustriesV2';

// Variant 3 Components (Interactive Growth Simulator)
import TTOSHeroV3 from './TTOSHeroV3';
import TTOSSimulatorV3 from './TTOSSimulatorV3';
import TTOSROICalculatorV3 from './TTOSROICalculatorV3';
import TTOSComparisonV3 from './TTOSComparisonV3';

interface TTOSLandingPageProps {
  initialVariant?: number;
}

function TTOSLandingContent({ initialVariant }: { initialVariant: number }) {
  const searchParams = useSearchParams();
  const variantParam = searchParams?.get('variant') || searchParams?.get('v');
  const [variant, setVariant] = useState<number>(initialVariant);

  useEffect(() => {
    if (variantParam) {
      const parsed = parseInt(variantParam, 10);
      if (parsed === 1 || parsed === 2 || parsed === 3) {
        setVariant(parsed);
      }
    }
  }, [variantParam]);

  const handleVariantChange = (newVariant: number) => {
    setVariant(newVariant);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans antialiased selection:bg-purple-500 selection:text-white overflow-x-hidden">
      {/* Top Header with Navigation */}
      <TTOSHeader />

      {/* Main Content Area Based on Active Variant */}
      <main className="transition-opacity duration-300">
        {variant === 1 && (
          <div className="animate-in fade-in duration-300">
            {/* Variant 1: Screenshot 1 Replica */}
            <TTOSHeroV1 />
            <TTOSIndustrySelector />
            <TTOSWayProcess />
            <TTOSEverythingGrid />
            <TTOSStatsRibbon />
            <TTOSTestimonials />
            <TTOSCTABanner />
          </div>
        )}

        {variant === 2 && (
          <div className="animate-in fade-in duration-300">
            {/* Variant 2: Screenshot 2 & 3 Replica */}
            <TTOSHeroV2 />
            <TTOSFeaturesV2 />
            <TTOSIndustriesV2 />
            <TTOSStatsRibbon />
            <TTOSTestimonials />
          </div>
        )}

        {variant === 3 && (
          <div className="animate-in fade-in duration-300">
            {/* Variant 3: Interactive Growth & Promptless Simulator */}
            <TTOSHeroV3 />
            <TTOSSimulatorV3 />
            <TTOSROICalculatorV3 />
            <TTOSComparisonV3 />
            <TTOSStatsRibbon />
            <TTOSCTABanner />
          </div>
        )}
      </main>

      {/* Modern Footer */}
      <TTOSFooter />
    </div>
  );
}

export const TTOSLandingPage: React.FC<TTOSLandingPageProps> = ({
  initialVariant = 1,
}) => {
  return (
    <React.Suspense fallback={<div className="min-h-screen bg-white" />}>
      <TTOSLandingContent initialVariant={initialVariant} />
    </React.Suspense>
  );
};

export default TTOSLandingPage;
